import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, CreditCard, Euro, Users, CheckCircle, Edit, X, Printer, ExternalLink, Copy, Check } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

const CotisationManagement = () => {
  const currentYear = new Date().getFullYear();
  const currentDate = new Date();
  
  // Déterminer l'année par défaut : si on est en décembre ou après, afficher l'année suivante
  const getDefaultYear = () => {
    const now = new Date();
    const month = now.getMonth() + 1; // Mois de 1 à 12
    const year = now.getFullYear();
    
    // Si on est en décembre (mois 12) ou après, afficher l'année suivante
    return month >= 12 ? year + 1 : year;
  };

  // Générer automatiquement les années (2024, 2025, puis années futures)
  const getAvailableYears = () => {
    const years = [2024, 2025];
    const defaultYear = getDefaultYear();
    
    // Ajouter toutes les années jusqu'à l'année par défaut
    for (let year = 2026; year <= defaultYear; year++) {
      years.push(year);
    }
    return years;
  };
  
  const [selectedYear, setSelectedYear] = useState(getDefaultYear());
  const [activeSubTab, setActiveSubTab] = useState('annuels');
  const [availableYears, setAvailableYears] = useState(getAvailableYears());
  const [allMembers, setAllMembers] = useState([]);
  const [cotisationsData, setCotisationsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [verifiedYears, setVerifiedYears] = useState(new Set()); // Pour tracker les années vérifiées
  const [copiedLink, setCopiedLink] = useState(null); // Pour tracker le lien copié
  const [generatingCotisations, setGeneratingCotisations] = useState(false); // Pour le bouton de génération

  // Charger TOUS les membres au démarrage et vérifier la génération automatique
  useEffect(() => {
    fetchAvailableYears();  // Charger les années disponibles depuis la BDD
    fetchAllMembers();
    checkAndAutoGenerate();
  }, []);

  useEffect(() => {
    if (allMembers.length > 0) {
      fetchCotisationsForYear();
      // Vérifier les paiements Stripe pour cette année si pas encore fait
      if (!verifiedYears.has(selectedYear)) {
        verifyStripePaymentsForYear(selectedYear);
      }
    }
  }, [selectedYear, allMembers]);

  const fetchAllMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken'); // Token unifié
      
      console.log('🔄 Chargement des membres avec token:', token ? 'présent' : 'absent');
      
      if (!token) {
        console.error('❌ Pas de token admin trouvé');
        setLoading(false);
        return;
      }
      
      // Récupérer TOUS les 455 membres de la base
      const membersResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?limit=1000`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('🔄 Réponse API status:', membersResponse.status);

      if (membersResponse.ok) {
        const members = await membersResponse.json();
        setAllMembers(members);
        
        console.log(`✅ ${members.length} membres chargés !`);
        console.log('🏷️ Répartition par rôle:', {
          annuels: members.filter(m => m.role === 'membre_annuel').length,
          vie: members.filter(m => m.role === 'membre_vie').length,
          exterieurs: members.filter(m => m.role === 'membre_exterieur').length,
          admin: members.filter(m => m.role === 'admin').length
        });
      } else {
        const errorText = await membersResponse.text();
        console.error('❌ Erreur API:', membersResponse.status, errorText);
      }
    } catch (error) {
      console.error('❌ Erreur chargement membres:', error);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier et générer automatiquement les cotisations si nécessaire
  const checkAndAutoGenerate = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      if (!token) return;

      // Vérifier le statut de la génération automatique
      const checkResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/check-auto-generation`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (checkResponse.ok) {
        const checkData = await checkResponse.json();
        console.log('📊 Statut génération automatique:', checkData);

        // Si on doit générer les cotisations
        if (checkData.should_generate) {
          console.log('🚀 Génération automatique des cotisations pour', checkData.next_year);
          
          const generateResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/auto-generate-next-year`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (generateResponse.ok) {
            const result = await generateResponse.json();
            console.log('✅ Génération automatique réussie:', result);
            
            // Recharger les cotisations
            if (allMembers.length > 0) {
              await fetchCotisationsForYear();
            }
          }
        } else {
          console.log('ℹ️ Génération automatique non nécessaire pour le moment');
        }
      }
    } catch (error) {
      console.error('❌ Erreur vérification auto-génération:', error);
    }
  };

  // Fonction pour générer manuellement les cotisations de l'année prochaine
  const handleGenerateNextYearCotisations = async () => {
    console.log('🔔 Bouton génération cotisations cliqué');
    const nextYear = currentYear + 1;
    console.log('📅 Année suivante:', nextYear);
    
    // Double confirmation
    const firstConfirm = window.confirm(
      `⚠️ ATTENTION: Vous allez générer les cotisations pour l'année ${nextYear}.\n\n` +
      `Cette action va copier tous les membres annuels et créer leurs cotisations pour ${nextYear}.\n\n` +
      `Voulez-vous continuer?`
    );
    
    console.log('✅ Première confirmation:', firstConfirm);
    if (!firstConfirm) return;
    
    const secondConfirm = window.confirm(
      `Confirmation finale:\n\n` +
      `Générer les cotisations ${nextYear} pour tous les membres annuels actifs?\n\n` +
      `Note: Si des cotisations ${nextYear} existent déjà, elles ne seront pas recréées.`
    );
    
    if (!secondConfirm) return;

    try {
      setGeneratingCotisations(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      if (!token) {
        alert('Erreur: Pas de token d\'authentification');
        return;
      }

      console.log(`🚀 Génération manuelle des cotisations ${nextYear}...`);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/auto-generate-next-year`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Génération réussie:', result);
        
        alert(
          `✅ Cotisations ${nextYear} générées avec succès!\n\n` +
          `• Cotisations créées: ${result.created}\n` +
          `• Déjà existantes: ${result.existing}\n` +
          `• Total membres: ${result.total_members}`
        );
        
        // Recharger la liste des années depuis la base de données
        await fetchAvailableYears();
        console.log(`✅ Liste des années rechargée depuis la base de données`);
        
        // Basculer automatiquement sur la nouvelle année
        setSelectedYear(nextYear);
        console.log(`✅ Basculement automatique vers l'année ${nextYear}`);
        
        // Recharger les données pour la nouvelle année
        await fetchAllMembers();
        await fetchCotisationsForYear(nextYear);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      console.error('❌ Erreur génération cotisations:', error);
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setGeneratingCotisations(false);
    }
  };


  // Récupérer les années qui ont des cotisations dans la base de données
  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/available-years`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const years = data.years || [2024, 2025];
        console.log('📅 Années disponibles:', years);
        setAvailableYears(years);
      }
    } catch (error) {
      console.error('Erreur chargement années disponibles:', error);
    }
  };

  const fetchCotisationsForYear = async (yearToFetch = null) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const targetYear = yearToFetch || selectedYear;
      
      console.log(`📥 Chargement des cotisations pour ${targetYear}...`);
      
      // Récupérer les cotisations pour l'année sélectionnée
      const cotisationsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/annuelle?year=${targetYear}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (cotisationsResponse.ok) {
        const cotisationsResult = await cotisationsResponse.json();
        const cotisations = cotisationsResult.cotisations || [];
        
        console.log(`✅ ${cotisations.length} membres chargés pour ${selectedYear}`);
        
        // Compter les cotisations Stripe (dans cotisation.payment_method)
        const stripeCount = cotisations.filter(c => c.cotisation?.payment_method === 'stripe').length;
        console.log(`   💳 Dont ${stripeCount} paiements Stripe`);
        
        setCotisationsData(prev => ({
          ...prev,
          [selectedYear]: cotisations
        }));
      } else {
        console.error(`❌ Erreur chargement cotisations: ${cotisationsResponse.status}`);
      }
      
    } catch (error) {
      console.error('❌ Erreur cotisations:', error);
    }
  };

  // Initier un paiement Stripe
  const handlePayment = async (memberId, packageId, amount = null) => {
    try {
      setPaymentLoading(true);
      const token = localStorage.getItem('token');
      
      // Déterminer l'URL de succès et d'annulation
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/admin?tab=cotisations&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/admin?tab=cotisations`;
      
      let paymentData;
      
      if (amount) {
        // Paiement avec montant personnalisé (donations)
        paymentData = {
          amount: amount,
          currency: 'EUR',
          success_url: successUrl,
          cancel_url: cancelUrl,
          metadata: {
            member_id: memberId,
            type: 'donation',
            source: 'admin_panel'
          }
        };
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/custom`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentData),
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.url;
        } else {
          throw new Error('Erreur lors de la création de la session de paiement');
        }
      } else {
        // Paiement avec package prédéfini
        paymentData = {
          package_id: packageId,
          success_url: successUrl,
          cancel_url: cancelUrl,
          metadata: {
            member_id: memberId,
            type: packageId,
            source: 'admin_panel'
          }
        };
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/session`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentData),
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.url;
        } else {
          throw new Error('Erreur lors de la création de la session de paiement');
        }
      }
    } catch (error) {
      console.error('Erreur paiement:', error);
      alert('Erreur lors de l\'initiation du paiement');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Plus besoin de bouton générer - tout est automatique

  // Organiser TOUS les membres par type selon leur rôle
  const getMembersByType = (type) => {
    const filtered = allMembers.filter(member => {
      switch (type) {
        case 'annuels':
          return member.role === 'membre_annuel';
        case 'vie':
          return member.role === 'membre_vie';
        case 'exterieurs':
          return member.role === 'membre_exterieur';
        default:
          return false;
      }
    });
    
    console.log(`📊 Membres ${type}:`, filtered.length, 'sur', allMembers.length, 'total');
    return filtered;
  };

  // Obtenir le statut de cotisation pour un membre - CORRIGÉ
  const getMemberCotisationStatus = (memberId) => {
    const yearCotisations = cotisationsData[selectedYear] || [];
    
    // L'endpoint retourne : { id: memberId, cotisation: {...} }
    // Donc on cherche par 'id', pas 'member_id'
    const memberData = yearCotisations.find(c => c.id === memberId);
    const memberCotisation = memberData?.cotisation;
    
    // Debug pour Guillaume ANCION
    const member = allMembers.find(m => m.id === memberId);
    if (member && member.first_name === 'Guillaume' && member.last_name === 'ANCION') {
      console.log(`🔍 Guillaume ANCION - memberData:`, memberData);
      console.log(`🔍 Guillaume ANCION - Cotisation:`, memberCotisation);
    }
    
    // Par défaut selon le type de membre
    const defaultStatus = { 
      status: 'à_vérifier', 
      payment_method: null, 
      payment_date: null,
      amount_paid: null 
    };
    
    return memberCotisation || defaultStatus;
  };

  // Calculer les statistiques pour l'année
  const calculateStats = () => {
    const annuels = getMembersByType('annuels');
    const vie = getMembersByType('vie');
    const exterieurs = getMembersByType('exterieurs');
    
    const annuelsPaid = annuels.filter(member => {
      const status = getMemberCotisationStatus(member.id);
      return status.status === 'payee' || status.status === 'paid';
    }).length;

    const annuelsAVerifier = annuels.filter(member => {
      const status = getMemberCotisationStatus(member.id);
      return status.status === 'à_vérifier' || !status.status || (status.status !== 'payee' && status.status !== 'paid' && status.status !== 'à_payer');
    }).length;
    
    const revenus = annuelsPaid * 30; // 30€ par cotisation annuelle payée
    
    return {
      annuels: { total: annuels.length, paid: annuelsPaid, aVerifier: annuelsAVerifier },
      vie: { total: vie.length },
      exterieurs: { total: exterieurs.length },
      revenus: revenus
    };
  };

  // Mettre à jour le statut de paiement
  const updatePaymentStatus = async (memberId, newStatus, paymentMethod = 'virement') => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken'); // Token unifié
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/update-status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            member_id: memberId,
            year: selectedYear,
            status: newStatus,
            payment_method: paymentMethod,
            payment_date: newStatus === 'payee' ? new Date().toISOString() : null
          }),
        }
      );

      if (response.ok) {
        fetchCotisationsForYear(); // Recharger
        setEditingMember(null);
      } else {
        throw new Error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  // Mettre à jour donation (pour membres à vie et extérieurs)
  const updateDonationStatus = async (memberId, newStatus, paymentMethod, amount) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/update-status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            member_id: memberId,
            year: selectedYear,
            status: newStatus,
            payment_method: paymentMethod,
            amount_paid: amount,
            payment_date: newStatus === 'payee' ? new Date().toISOString() : null
          }),
        }
      );

      if (response.ok) {
        fetchCotisationsForYear(); // Recharger
        setEditingMember(null);
      } else {
        throw new Error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur mise à jour donation:', error);
      alert('Erreur lors de la mise à jour de la donation');
    }
  };

  // Vérifier les paiements Stripe pour une année spécifique (UNE SEULE FOIS par année)
  const verifyStripePaymentsForYear = async (year) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      if (!token) {
        console.warn('⚠️ Pas de token d\'authentification');
        return;
      }
      
      console.log(`🔍 Vérification des paiements Stripe pour ${year}...`);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/verify-stripe-payments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log(`📊 Résultat vérification:`, result);
        
        if (result.updated_count > 0) {
          console.log(`✅ ${result.updated_count} paiement(s) Stripe mis à jour`);
          // FORCER un rechargement IMMÉDIAT et COMPLET des données
          setTimeout(async () => {
            console.log('🔄 Rechargement des cotisations...');
            await fetchCotisationsForYear();
            console.log('✅ Cotisations rechargées');
          }, 1500);
        } else {
          console.log('ℹ️ Aucun nouveau paiement à mettre à jour');
        }
      } else {
        console.error(`❌ Erreur HTTP ${response.status}`);
      }
      
      // Marquer l'année comme vérifiée
      setVerifiedYears(prev => new Set([...prev, year]));
    } catch (error) {
      console.error('❌ Erreur vérification Stripe:', error);
      // Marquer quand même comme vérifié pour éviter les retry infinis
      setVerifiedYears(prev => new Set([...prev, year]));
    }
  };

  // Affichage du statut - SIMPLIFIÉ
  const renderStatus = (member) => {
    try {
      const cotisationStatus = getMemberCotisationStatus(member.id);
      
      // Pour les membres à vie et extérieurs
      if (member.role === 'membre_vie' || member.role === 'membre_exterieur') {
        if (cotisationStatus && (cotisationStatus.status === 'payee' || cotisationStatus.status === 'paid')) {
          return <Badge className="bg-green-100 text-green-800">Donation reçue</Badge>;
        }
        return <span className="text-gray-400">-</span>;
      }
      
      // Pour les membres annuels
      if (!cotisationStatus || cotisationStatus.status === 'à_vérifier') {
        return <Badge className="bg-orange-100 text-orange-800">À vérifier</Badge>;
      }
      
      switch (cotisationStatus.status) {
        case 'payee':
        case 'paid':
          // Si payé via Stripe, afficher badge vert avec ✓
          if (cotisationStatus.payment_method === 'stripe') {
            return <Badge className="bg-green-500 text-white">Stripe ✓</Badge>;
          }
          return <Badge className="bg-green-100 text-green-800">Payé</Badge>;
        case 'à_payer':
        case 'pending':
          return <Badge className="bg-yellow-100 text-yellow-800">Paiement en attente</Badge>;
        default:
          return <Badge className="bg-orange-100 text-orange-800">À vérifier</Badge>;
      }
    } catch (error) {
      console.error('Erreur renderStatus:', error);
      return <Badge className="bg-gray-100 text-gray-800">Erreur</Badge>;
    }
  };

  // Affichage du montant de donation pour membres à vie/extérieurs
  const renderDonationAmount = (member) => {
    const cotisationStatus = getMemberCotisationStatus(member.id);
    const isEditing = editingMember === member.id;
    
    if (isEditing) {
      return (
        <div className="flex flex-col space-y-2">
          <div className="flex items-center space-x-1">
            <input
              type="number"
              placeholder="Montant €"
              className="w-20 px-2 py-1 text-xs border rounded"
              id={`amount-${member.id}`}
            />
            <Button
              size="sm"
              onClick={() => {
                const amount = document.getElementById(`amount-${member.id}`).value;
                const method = document.querySelector(`input[name="method-${member.id}"]:checked`)?.value || 'virement';
                if (amount && parseFloat(amount) > 0) {
                  updateDonationStatus(member.id, 'payee', method, parseFloat(amount));
                }
              }}
              className="text-xs px-2 py-1 bg-green-500 text-white"
            >
              ✓
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingMember(null)}
              className="text-xs px-1 py-1"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex space-x-2 text-xs">
            <label className="flex items-center">
              <input
                type="radio"
                name={`method-${member.id}`}
                value="virement"
                defaultChecked
                className="mr-1"
              />
              Virement
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={`method-${member.id}`}
                value="liquide"
                className="mr-1"
              />
              Liquide
            </label>
          </div>
        </div>
      );
    }

    if ((cotisationStatus.status === 'payee' || cotisationStatus.status === 'paid') && cotisationStatus.amount_paid) {
      return <span className="text-sm font-medium">{cotisationStatus.amount_paid}€</span>;
    }

    return <span className="text-gray-400">-</span>;
  };

  // Affichage de la méthode de paiement (pour membres annuels)
  const renderPaymentMethod = (member) => {
    const cotisationStatus = getMemberCotisationStatus(member.id);
    const isEditing = editingMember === member.id;
    
    // Pour les membres annuels seulement
    if (member.role === 'membre_annuel') {
      if (isEditing) {
        return (
          <div className="flex space-x-1 flex-wrap">
            <Button
              size="sm"
              onClick={() => updatePaymentStatus(member.id, 'payee', 'stripe')}
              className="text-xs px-2 py-1 bg-green-600 text-white hover:bg-green-700"
            >
              Stripe
            </Button>
            <Button
              size="sm"
              onClick={() => updatePaymentStatus(member.id, 'payee', 'virement')}
              className="text-xs px-2 py-1 bg-blue-500 text-white"
            >
              Virement
            </Button>
            <Button
              size="sm"
              onClick={() => updatePaymentStatus(member.id, 'payee', 'liquide')}
              className="text-xs px-2 py-1 bg-green-500 text-white"
            >
              Liquide
            </Button>
            <Button
              size="sm"
              onClick={() => updatePaymentStatus(member.id, 'à_vérifier', 'à_vérifier')}
              className="text-xs px-2 py-1 bg-orange-500 text-white"
            >
              À vérifier
            </Button>
            <Button
              size="sm"
              onClick={() => updatePaymentStatus(member.id, 'à_payer', null)}
              className="text-xs px-2 py-1 bg-yellow-500 text-white"
            >
              Paiement en attente
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingMember(null)}
              className="text-xs px-1 py-1"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        );
      }

      if (cotisationStatus.status === 'payee' || cotisationStatus.status === 'paid') {
        const method = cotisationStatus.payment_method || 'Non spécifié';
        return <span className="text-sm">{method.charAt(0).toUpperCase() + method.slice(1)}</span>;
      }
    }

    return <span className="text-gray-400">-</span>;
  };

  // Actions d'édition
  const renderActions = (member) => {
    const cotisationStatus = getMemberCotisationStatus(member.id);
    
    return (
      <div className="flex space-x-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setEditingMember(member.id)}
          className="p-1"
        >
          <Edit className="w-3 h-3" />
        </Button>
        {member.role === 'membre_annuel' && (
          <>
            {cotisationStatus.status !== 'payee' && cotisationStatus.status !== 'paid' && (
              <>
                <Button
                  size="sm"
                  onClick={() => {
                    // Ouvrir le lien court de paiement dans un nouvel onglet
                    const paymentUrl = `${process.env.REACT_APP_BACKEND_URL}/api/pay/${selectedYear}/${member.id}`;
                    window.open(paymentUrl, '_blank');
                  }}
                  className="text-xs px-2 py-1 bg-[rgb(196,150,96)] text-white hover:bg-[rgb(176,130,76)]"
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Paiement
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    // Copier le lien de paiement dans le presse-papiers
                    const paymentUrl = `${process.env.REACT_APP_BACKEND_URL}/api/pay/${selectedYear}/${member.id}`;
                    navigator.clipboard.writeText(paymentUrl).then(() => {
                      setCopiedLink(member.id);
                      setTimeout(() => setCopiedLink(null), 2000);
                    });
                  }}
                  className={`text-xs px-2 py-1 ${copiedLink === member.id ? 'bg-green-600' : 'bg-blue-600'} text-white hover:bg-opacity-80`}
                  title="Copier le lien de paiement"
                >
                  {copiedLink === member.id ? (
                    <>
                      <Check className="w-3 h-3 mr-1" />
                      Copié !
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3 mr-1" />
                      Copier lien
                    </>
                  )}
                </Button>
              </>
            )}
            <Button
              size="sm"
              onClick={() => {
                // Générer et imprimer la lettre de cotisation
                const printWindow = window.open('', '_blank', 'width=800,height=900');
                const paymentUrl = `${process.env.REACT_APP_BACKEND_URL}/api/pay/${selectedYear}/${member.id}`;
                printWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>Invitation au paiement - Cotisation ${selectedYear}</title>
                    <style>
                      @page {
                        size: A4;
                        margin: 15mm;
                      }
                      body {
                        font-family: 'Georgia', serif;
                        padding: 15px 30px;
                        color: #333;
                        line-height: 1.4;
                        max-width: 800px;
                        margin: 0 auto;
                        font-size: 14px;
                      }
                      .header {
                        text-align: center;
                        margin-bottom: 25px;
                        border-bottom: 2px solid rgb(196, 150, 96);
                        padding-bottom: 10px;
                      }
                      .logo {
                        max-width: 80px;
                        margin-bottom: 8px;
                      }
                      h1 {
                        color: rgb(196, 150, 96);
                        font-size: 18px;
                        margin: 3px 0;
                        font-weight: normal;
                      }
                      h2 {
                        color: rgb(196, 150, 96);
                        font-size: 16px;
                        margin-top: 20px;
                        margin-bottom: 10px;
                      }
                      p {
                        margin-bottom: 10px;
                        text-align: justify;
                        font-size: 14px;
                      }
                      .greeting {
                        margin-top: 20px;
                        margin-bottom: 15px;
                      }
                      .amount-box {
                        background-color: #f9fafb;
                        border: 2px solid rgb(196, 150, 96);
                        border-radius: 6px;
                        padding: 15px;
                        margin: 20px 0;
                        text-align: center;
                      }
                      .amount {
                        font-size: 28px;
                        font-weight: bold;
                        color: rgb(196, 150, 96);
                        margin: 8px 0;
                      }
                      .payment-link {
                        background-color: #e0f2fe;
                        border-left: 4px solid rgb(196, 150, 96);
                        padding: 12px;
                        margin: 20px 0;
                      }
                      .payment-link a {
                        color: rgb(196, 150, 96);
                        font-weight: bold;
                        font-size: 13px;
                        word-break: break-all;
                      }
                      .footer {
                        margin-top: 30px;
                        padding-top: 15px;
                        border-top: 2px solid rgb(196, 150, 96);
                        text-align: center;
                      }
                      .signature {
                        margin-top: 20px;
                        font-style: italic;
                      }
                      @media print {
                        body {
                          padding: 10px 20px;
                        }
                        .header {
                          margin-bottom: 20px;
                        }
                        .amount-box {
                          margin: 15px 0;
                          padding: 12px;
                        }
                        .payment-link {
                          margin: 15px 0;
                          padding: 10px;
                        }
                        .footer {
                          margin-top: 25px;
                        }
                      }
                    </style>
                  </head>
                  <body>
                    <div class="header">
                      <img src="${process.env.REACT_APP_BACKEND_URL}/images/lignages-logo-admin.png" alt="Logo" class="logo">
                      <h1>Les Lignages de Bruxelles</h1>
                    </div>

                    <div class="greeting">
                      <p>À l'attention de ${member.first_name} ${member.last_name},</p>
                    </div>

                    <p>
                      Nous vous remercions de votre engagement envers les Lignages de Bruxelles et vous invitons à régler votre cotisation annuelle pour l'année <strong>${selectedYear}</strong>.
                    </p>

                    <div class="amount-box">
                      <p style="margin: 0; font-size: 13px; color: #666;">Montant de la cotisation</p>
                      <div class="amount">${cotisationStatus.amount_due || 30}€</div>
                      <p style="margin: 0; font-size: 13px; color: #666;">Année ${selectedYear}</p>
                    </div>

                    <h2>💳 Paiement en ligne sécurisé</h2>

                    <p>
                      Pour faciliter votre paiement, nous avons mis en place un système de paiement en ligne sécurisé via <strong>Stripe</strong>. Pour effectuer votre paiement, veuillez suivre le lien ci-dessous :
                    </p>

                    <div class="payment-link">
                      <p style="margin: 0 0 8px 0; font-size: 13px; font-weight: bold;">🔗 Votre lien de paiement personnel :</p>
                      <a href="${paymentUrl}" target="_blank">${paymentUrl}</a>
                      <p style="margin: 10px 0 0 0; font-size: 11px; color: #666;">
                        Ce lien est personnel et sécurisé. Vous recevrez une confirmation par email dès que votre paiement sera enregistré.
                      </p>
                    </div>

                    <div class="footer">
                      <div class="signature">
                        <p style="margin: 5px 0;">Avec nos salutations distinguées,</p>
                        <p style="margin: 5px 0;"><strong>Le Secrétariat des Lignages de Bruxelles</strong></p>
                      </div>
                      
                      <p style="margin-top: 15px; font-size: 12px; color: #666;">
                        ✉️ contact@lignagesdebruxelles.be | 🌐 https://lignagesdebruxelles.be
                      </p>
                    </div>

                    <script>
                      window.onload = function() {
                        window.print();
                      };
                      
                      window.onafterprint = function() {
                        window.close();
                      };
                      
                      if (window.matchMedia) {
                        const mediaQueryList = window.matchMedia('print');
                        mediaQueryList.addListener(function(mql) {
                          if (!mql.matches) {
                            window.close();
                          }
                        });
                      }
                    </script>
                  </body>
                  </html>
                `);
                printWindow.document.close();
              }}
              variant="outline"
              size="sm"
              className="text-xs px-2 py-1"
            >
              <Printer className="w-3 h-3 mr-1" />
              Impression
            </Button>
          </>
        )}
      </div>
    );
  };

  // DataTable avec tri et recherche (simple version)
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('last_name');
  const [sortDirection, setSortDirection] = useState('asc');

  const filteredAndSortedMembers = (memberType) => {
    let members = getMembersByType(memberType);
    
    // Filtrage par recherche
    if (searchTerm) {
      members = members.filter(member => 
        `${member.first_name} ${member.last_name} ${member.email}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Tri
    members.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'name') {
        aValue = `${a.first_name} ${a.last_name}`;
        bValue = `${b.first_name} ${b.last_name}`;
      }
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
    
    return members;
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ color: 'rgb(196, 150, 96)' }}>
            Gestion des Cotisations
          </h1>
          <p className="text-gray-600">
            Tous les membres classés par année avec DataTables
          </p>
        </div>
        
        {/* Bouton Générer Cotisations pour l'année prochaine */}
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('🖱️ CLIC BOUTON DÉTECTÉ !');
            handleGenerateNextYearCotisations();
          }}
          disabled={generatingCotisations}
          className="bg-green-600 hover:bg-green-700 text-white"
          type="button"
        >
          {generatingCotisations ? 'Génération en cours...' : `Générer les Cotisations ${currentYear + 1}`}
        </Button>
      </div>


      {/* Texte explicatif sur la génération automatique */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Génération automatique des cotisations
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Les cotisations pour l'année suivante ({currentYear + 1}) seront automatiquement générées le <strong>1er décembre {currentYear}</strong>.
              </p>
              <p className="mt-1">
                Vous pouvez également générer manuellement les cotisations de l'année suivante en cliquant sur le bouton "Générer les Cotisations {currentYear + 1}" ci-dessus.
              </p>
            </div>
          </div>
        </div>
      </div>


      {/* Vérifier les paramètres URL pour les retours de paiement */}
      {new URLSearchParams(window.location.search).get('session_id') && (
        <Alert className="mb-6 border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Paiement effectué avec succès ! Les informations de cotisation ont été mises à jour.
          </AlertDescription>
        </Alert>
      )}

      {/* ONGLETS ANNÉES EN HAUT */}
      <Tabs value={selectedYear.toString()} onValueChange={(year) => setSelectedYear(parseInt(year))}>
        <TabsList className="w-fit mb-6">
          {availableYears.map(year => (
            <TabsTrigger key={year} value={year.toString()}>
              {year}
            </TabsTrigger>
          ))}
        </TabsList>

        {availableYears.map(year => (
          <TabsContent key={year} value={year.toString()}>
            <div className="space-y-6">
              {/* CARTES STATISTIQUES */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600">Membres annuels</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" style={{ color: 'rgb(196, 150, 96)' }}>
                      {stats.annuels.total}
                    </div>
                    <p className="text-xs text-gray-500">
                      {stats.annuels.paid} payés / {stats.annuels.aVerifier} à vérifier
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600">Membres à vie</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {stats.vie.total}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600">Membres extérieurs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">
                      {stats.exterieurs.total}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600">Revenus {year}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {stats.revenus}€
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* SOUS-ONGLETS PAR TYPE DE MEMBRE */}
              <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="annuels">Membres annuels</TabsTrigger>
                  <TabsTrigger value="vie">Membres à vie</TabsTrigger>
                  <TabsTrigger value="exterieurs">Membres extérieurs</TabsTrigger>
                </TabsList>

                {/* Barre de recherche */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Rechercher un membre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>

                {/* TABLE MEMBRES ANNUELS */}
                <TabsContent value="annuels">
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead 
                            onClick={() => setSortField('first_name')}
                            className="cursor-pointer hover:bg-gray-50"
                          >
                            Prénom
                          </TableHead>
                          <TableHead 
                            onClick={() => setSortField('last_name')}
                            className="cursor-pointer hover:bg-gray-50"
                          >
                            Nom
                          </TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Paiement</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAndSortedMembers('annuels').length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                              {allMembers.length === 0 ? 'Chargement des membres...' : 'Aucun membre annuel trouvé'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredAndSortedMembers('annuels').map(member => (
                            <TableRow key={member.id}>
                              <TableCell>{member.first_name}</TableCell>
                              <TableCell className="font-medium">{member.last_name}</TableCell>
                              <TableCell>{member.email || 'N/A'}</TableCell>
                              <TableCell>{renderStatus(member)}</TableCell>
                              <TableCell>{renderPaymentMethod(member)}</TableCell>
                              <TableCell>{renderActions(member)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Card>
                </TabsContent>

                {/* TABLE MEMBRES À VIE */}
                <TabsContent value="vie">
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead 
                            onClick={() => setSortField('first_name')}
                            className="cursor-pointer hover:bg-gray-50"
                          >
                            Prénom
                          </TableHead>
                          <TableHead 
                            onClick={() => setSortField('last_name')}
                            className="cursor-pointer hover:bg-gray-50"
                          >
                            Nom
                          </TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Montant donation</TableHead>
                          <TableHead>Montant</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAndSortedMembers('vie').length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                              {allMembers.length === 0 ? 'Chargement des membres...' : 'Aucun membre à vie trouvé'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredAndSortedMembers('vie').map(member => (
                            <TableRow key={member.id}>
                              <TableCell>{member.first_name}</TableCell>
                              <TableCell className="font-medium">{member.last_name}</TableCell>
                              <TableCell>{member.email || 'N/A'}</TableCell>
                              <TableCell>{renderStatus(member)}</TableCell>
                              <TableCell>{renderDonationAmount(member)}</TableCell>
                              <TableCell>{renderActions(member)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Card>
                </TabsContent>

                {/* TABLE MEMBRES EXTÉRIEURS */}
                <TabsContent value="exterieurs">
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead 
                            onClick={() => setSortField('first_name')}
                            className="cursor-pointer hover:bg-gray-50"
                          >
                            Prénom
                          </TableHead>
                          <TableHead 
                            onClick={() => setSortField('last_name')}
                            className="cursor-pointer hover:bg-gray-50"
                          >
                            Nom
                          </TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Montant donation</TableHead>
                          <TableHead>Montant</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAndSortedMembers('exterieurs').length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                              {allMembers.length === 0 ? 'Chargement des membres...' : 'Aucun membre extérieur trouvé'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredAndSortedMembers('exterieurs').map(member => (
                            <TableRow key={member.id}>
                              <TableCell>{member.first_name}</TableCell>
                              <TableCell className="font-medium">{member.last_name}</TableCell>
                              <TableCell>{member.email || 'N/A'}</TableCell>
                              <TableCell>{renderStatus(member)}</TableCell>
                              <TableCell>{renderDonationAmount(member)}</TableCell>
                              <TableCell>{renderActions(member)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default CotisationManagement;